/**
 * DimensionValuePicker — Searchable, hierarchical picker for a value within one
 * analytic dimension (e.g. Sales › EMEA Sales under "Cost Center").
 *
 * Built on EntityPickerBase: keyboard-navigable + ARIA-correct per STYLE_GUIDE.
 * Values arrive from the API already ordered by hierarchy path; we indent each
 * row by its `depth`. Search filters by name / code / path.
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityPickerBase } from '../common';
import { searchScore } from '../../utils/searchScore';
import {
  useDimension,
  useDimensionValues,
} from '../../../modules/accounting/hooks/useDimensions';
import { DimensionValueQuickCreateDialog } from '../../../modules/accounting/components/DimensionValueQuickCreateDialog';
import type { DimensionValue } from '../../../modules/accounting/types';

interface DimensionValuePickerProps {
  /** The dimension (axis) whose values are offered */
  dimensionId: string;
  /** Currently selected value ID */
  value: string;
  /** Callback when a value is selected (empty string when cleared) */
  onChange: (valueId: string) => void;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder text when nothing is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show a "None" option to clear the selection */
  allowClear?: boolean;
  /** Show required indicator on label */
  required?: boolean;
  /** Exclude a value and its descendants (used to prevent cyclic re-parenting) */
  excludeSubtreeOf?: string;
  /** Hide the inline "Add new" action (e.g. in filter contexts or the value form's own parent picker) */
  hideAddNew?: boolean;
}

export function DimensionValuePicker({
  dimensionId,
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  allowClear = false,
  required,
  excludeSubtreeOf,
  hideAddNew = false,
}: DimensionValuePickerProps) {
  const { t } = useTranslation('accounting');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');

  const { data } = useDimensionValues(dimensionId);
  // The value form dialog needs the full axis (for hierarchy + scoping). Only
  // fetched when inline create is enabled and a dimension is selected.
  const { data: dimensionData } = useDimension(hideAddNew ? '' : dimensionId);
  const dimension = dimensionData?.data ?? null;

  const values = useMemo(() => {
    let all: DimensionValue[] = data?.data ?? [];
    all = all.filter((v) => v.is_active);

    // Exclude a node and its descendants (re-parent guard): the descendants are
    // exactly those whose path is prefixed by the excluded node's path.
    if (excludeSubtreeOf) {
      const node = all.find((v) => v.id === excludeSubtreeOf);
      if (node) {
        const prefix = node.path ? `${node.path}/` : '';
        all = all.filter(
          (v) => v.id !== excludeSubtreeOf && !(prefix && v.path.startsWith(prefix)),
        );
      }
    }

    // Scored rather than sorted: values arrive ordered by hierarchy path and each
    // row is indented by its depth, so relevance re-ordering would leave a child
    // indented under an unrelated parent. Keep the API's hierarchy order and use
    // the shared matcher only to decide membership (multi-word, partial, stem and
    // typo matching all still apply).
    if (!search.trim()) return all;
    return all.filter(
      (v) => searchScore([v.name, v.code, v.path].filter(Boolean).join(' '), search) > 0,
    );
  }, [data, search, excludeSubtreeOf]);

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
    setSearch('');
  };

  const handleAddNew = () => {
    setCreateName(search.trim());
    setOpen(false);
    setShowCreate(true);
  };

  const handleCreated = (created: DimensionValue) => {
    onChange(created.id);
    setSearch('');
    setShowCreate(false);
  };

  const addNewLabel = search.trim()
    ? t('dimensions.createWithName', { name: search.trim() })
    : t('dimensions.addNewValue');

  // Inline create needs a selected, loaded axis to scope the new value to.
  const canAddNew = !hideAddNew && !!dimension;

  return (
    <>
      <EntityPickerBase<DimensionValue>
        value={value}
        items={values}
        getItemLabel={(v) => v.name}
        renderItem={(v) => (
          // Indent by depth to convey hierarchy; flatten when searching is fine
          // since the path is still discoverable via the code suffix.
          <span
            className="flex min-w-0 items-center"
            style={{ paddingInlineStart: `${v.depth * 12}px` }}
          >
            <span className="truncate">{v.name}</span>
            <span className="text-muted-foreground ms-1.5 shrink-0 text-xs">{v.code}</span>
          </span>
        )}
        label={label}
        required={required}
        placeholder={placeholder ?? t('dimensions.selectValue')}
        searchPlaceholder={t('dimensions.searchValue')}
        emptyMessage={t('dimensions.noValueFound')}
        disabled={disabled || !dimensionId}
        className={className}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        addNewLabel={addNewLabel}
        onAddNew={canAddNew ? handleAddNew : undefined}
      />

      {dimension && (
        <DimensionValueQuickCreateDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          dimension={dimension}
          onCreated={handleCreated}
          defaultName={createName}
        />
      )}
    </>
  );
}
